import React, { useState, useRef } from 'react';
import { CSVLink } from 'react-csv';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { numericFormatter } from 'react-number-format';
import moment from 'moment';
import { map } from 'ramda';

import Button from '../Button';

import { formatFilters, formatPhone } from '../../helpers/formatters';
import { renameProp } from '../../helpers/common';
import { formatPermanence, formatServicesCategories } from '../../helpers/formatters';

import {
  convertList,
  convertToOptions,
  convertToBrl,
  convertToReal,
  convertToEstablishmentName
} from '../../helpers/converters';

export default function ExportCSV({ fileName, disabled, exportType, requestData, exportHeaders, formatComponentData }) {
  const exportButton = useRef(null);

  const [exportData, setExportData] = useState([]);
  const [operationalExportData, setOperationalExportData] = useState([]);

  const [headers] = useState(map(renameProp('id', 'key'), exportHeaders));

  const { qp, selects, noEstablishmentFilters, isExporting } = useSelector(state => state.filters);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const { establishments } = useSelector(state => state.login);
  const { areasOfUse } = useSelector(state => state.products.filters);
  const { servicesCategories } = useSelector(state => state.services);

  const invoiceSituations = selects?.find(filter => filter?.id == 'clientInvoiceSituation')?.options;
  const availableCategories = selects?.find(filter => filter?.id == 'category')?.options;
  const availablePaymentMethods = convertToOptions(['paymentMethod'], selects);

  const establishmentName =
    convertToEstablishmentName(establishmentId, establishments) ||
    'Todos os estabelecimentos';

  const getEstablishmentExportName = exportType => {
    if(exportType == 'clients') {
      return `${establishmentName}-clientes.xls`;
    }

    if(exportType == 'productsSell') {
      return `${establishmentName}-produtos.xls`;
    }

    if(exportType == 'productsConsume') {
      return `${establishmentName}-produtos.xls`;
    }

    if(exportType == 'sellOrders') {
      return `${establishmentName}-historico-de-venda.xls`;
    }

    if(exportType == 'supplier') {
      return `fornecedores-${establishmentName}.xls`;
    }

    return establishmentName;
  }

  const handleExportation = () => {
    const wholeData = { page: '', pageSize: '', exportation: true, qp };

    requestData(wholeData, 'xls')
      .then(async data => {
        if(exportType == 'comissioned') {
          const payrollSituations = data?.payrollSituations?.options;

          const formattedExportData = data?.data?.map(data => {
            return {
              name: data?.name,
              roleName: data?.roleName,
              situationId: payrollSituations?.find(situation => situation?.id == data?.payroll?.situationId)?.name || '',
              dueDate: moment(data?.payroll?.dueDate).format('DD/MM/YYYY'),
              amount: data?.payroll?.amount ? `R$ ${data?.payroll?.amount}` : ''
            }
          });

          await setExportData(formattedExportData);
        }

        if(exportType == 'operationalExport') {
          const {
            resume: { totalUpdated, totalSoCanceled, totalPaid, totalAmount, totalFreeSituations },
            search: { startDateTime, endDateTime },
            establishment,
            content,
            mediumPeriod,
            totalPeriod
          } = data;

          const formattedContent = content?.map((item, index) => {
            return {
              ...item,
              ...index == 0 ? {
                startDateTime,
                endDateTime,
                totalUpdated,
                totalSoAmount: totalAmount,
                totalPaid,
                totalSoCanceled,
                totalFreeSituations,
                totalPeriod,
                mediumPeriod
              } : {}
            }
          });

          const formattedExportData = !establishment
            ? formattedContent?.map(data => {
                return [
                  data?.establishmentName || '',
                  data?.serviceOrderCode || '',
                  data?.clientName || '',
                  !!data?.clientPhone ? formatPhone(JSON.parse(data?.clientPhone)) : '',
                  data?.clientEmail || '',
                  data?.plate || '',
                  data?.vehicleModel || '',
                  data?.vehicleColor || '',
                  !!data?.cardCode ? data?.cardCode : '',
                  moment(data?.entryDateTime, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY'),
                  moment(data?.entryDateTime, 'YYYY-MM-DD HH:mm:ss').format('HH:mm:ss'),
                  data?.exitDateTime == '0001-01-01 00:00:00' ? '' : moment(data?.exitDateTime, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY'),
                  data?.exitDateTime?.split(' ')[1] == '00:00:00' ? '' : moment(data?.exitDateTime, 'YYYY-MM-DD HH:mm:ss').format('HH:mm:ss'),
                  formatPermanence([data?.exitDateTime, data?.entryDateTime]),
                  data?.userName,
                  data?.userOutputName,
                  data?.typePrice || '',
                  data?.services?.length > 0 ? data?.services?.map(service => `${service?.description} ${!!service?.quantity ? `(${service?.quantity})` : ''} - ${convertToBrl(+service?.amount)}`)?.join(', ') : '',
                  data?.products?.length > 0 ? data?.products?.map(product => `${product?.description} ${!!product?.quantity ? `(${parseInt(product?.quantity)})` : ''} - ${convertToBrl(+product?.amount)}`)?.join(', ') : '',
                  data?.paymentMethodName || '',
                  data?.observations?.observation || '',
                  data?.discountAmount ? convertToBrl(+data?.discountAmount || 0) : '',
                  !!totalAmount ? parseFloat(data?.totalAmount)?.toFixed(2)?.toString()?.replace('.', ',') : '',
                  !!data?.startDateTime ? `${moment(data?.startDateTime, 'DD/MM/YYYY').format('DD/MM/YYYY')} - ${moment(data?.endDateTime, 'DD/MM/YYYY').format('DD/MM/YYYY')}` : '',
                  !!data?.totalUpdated ? data?.totalUpdated?.toString() : '',
                  !!data?.totalPaid ? data?.totalPaid?.toString() : '',
                  !!data?.totalSoCanceled ? data?.totalSoCanceled?.toString() : '',
                  !!data?.totalSoAmount ? convertToBrl(parseFloat(data?.totalSoAmount))?.toString() : '',
                  !!data?.totalFreeSituations ? data?.totalFreeSituations?.toString() : '',
                  !!data?.totalPeriod ? data?.totalPeriod : '',
                  !!data?.mediumPeriod ? data?.mediumPeriod : ''
                ];
              })
            : formattedContent?.map(data => {
                return [
                  data?.serviceOrderCode || '',
                  data?.clientName || '',
                  !!data?.clientPhone ? formatPhone(JSON.parse(data?.clientPhone)) : '',
                  data?.clientEmail || '',
                  data?.plate || '',
                  data?.vehicleModel || '',
                  data?.vehicleColor || '',
                  !!data?.cardCode ? data?.cardCode : '',
                  moment(data?.entryDateTime, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY'),
                  moment(data?.entryDateTime, 'YYYY-MM-DD HH:mm:ss').format('HH:mm:ss'),
                  data?.exitDateTime == '0001-01-01 00:00:00' ? '' : moment(data?.exitDateTime, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY'),
                  data?.exitDateTime?.split(' ')[1] == '00:00:00' ? '' : moment(data?.exitDateTime, 'YYYY-MM-DD HH:mm:ss').format('HH:mm:ss'),
                  formatPermanence([data?.exitDateTime, data?.entryDateTime]),
                  data?.userName,
                  data?.userOutputName,
                  data?.typePrice || '',
                  data?.services?.length > 0 ? data?.services?.map(service => `${service?.description} ${!!service?.quantity ? `(${service?.quantity})` : ''} - ${convertToBrl(+service?.amount)}`)?.join(', ') : '',
                  data?.products?.length > 0 ? data?.products?.map(product => `${product?.description} ${!!product?.quantity ? `(${parseInt(product?.quantity)})` : ''} - ${convertToBrl(+product?.amount)}`)?.join(', ') : '',
                  data?.paymentMethodName || '',
                  data?.observations?.observation || '',
                  data?.discountAmount ? convertToBrl(+data?.discountAmount || 0) : '',
                  !!totalAmount ? parseFloat(data?.totalAmount)?.toFixed(2)?.toString()?.replace('.', ',') : '',
                  !!data?.startDateTime ? `${moment(data?.startDateTime, 'DD/MM/YYYY').format('DD/MM/YYYY')} - ${moment(data?.endDateTime, 'DD/MM/YYYY').format('DD/MM/YYYY')}` : '',
                  !!data?.totalUpdated ? data?.totalUpdated?.toString() : '',
                  !!data?.totalPaid ? data?.totalPaid?.toString() : '',
                  !!data?.totalSoCanceled ? data?.totalSoCanceled?.toString() : '',
                  !!data?.totalSoAmount ? convertToBrl(parseFloat(data?.totalSoAmount))?.toString() : '',
                  !!data?.totalFreeSituations ? data?.totalFreeSituations?.toString() : '',
                  !!data?.totalPeriod ? data?.totalPeriod : '',
                  !!data?.mediumPeriod ? data?.mediumPeriod : ''
                ];
              });

          const operationalExportFormattedData = [
            !establishment
              ? ['Estabelecimento', 'Nº', 'Cliente', 'Telefone', 'Email', 'Placa', 'Modelo', 'Cor', 'Cartão', 'Data Entrada', 'Hora Entrada', 'Data Saída', 'Hora Saída', 'Permanência', 'Operador', 'Operador Saída', 'Cobrança', 'Serviços', 'Produtos', 'Pagamento', 'Observação', 'Desconto', 'Valor', 'Período', 'Total de OS alterada', 'Total de OS paga', 'Total de OS cancelada', 'Total de OS', 'Total de OS isenta', 'Período total', 'Período médio']
              : ['Nº', 'Cliente', 'Telefone', 'Email', 'Placa', 'Modelo', 'Cor', 'Cartão', 'Data Entrada', 'Hora Entrada', 'Data Saída', 'Hora Saída', 'Permanência', 'Operador', 'Operador Saída', 'Cobrança', 'Serviços', 'Produtos', 'Pagamento', 'Observação', 'Desconto', 'Valor', 'Período', 'Total de OS alterada', 'Total de OS paga', 'Total de OS cancelada', 'Total de OS', 'Total de OS isenta', 'Período total', 'Período médio'],
            ...formattedExportData
          ];

          setOperationalExportData(operationalExportFormattedData);
        }

        if(exportType == 'clients') {
          const exportedData = await convertList(
            ['category'],
            formatFilters(['category'], selects),
            formatComponentData(exportType == 'extract' ? data : data.content, true)
          );

          const getSituationId = (clientTypeId, clientInvoiceSituationId, dueDateTime) => {
            if(+clientTypeId == 1) {
              return '';
            }

            return moment(dueDateTime, 'DD/MM/YYYY').isBefore(moment(), 'day') && +clientInvoiceSituationId == 1 ? 2 : 1;
          }

          const formattedData = exportedData?.map(content => {
            return {
              ...content,
              vehicles: content?.vehicles?.join(", "),
              paymentMethodId: availablePaymentMethods?.find(paymentMethod => +paymentMethod?.value == +content?.paymentMethodId)?.label,
              clientInvoiceSituation: !!content?.clientInvoiceSituationId ? invoiceSituations?.find(situation => +situation?.value == getSituationId(+content?.clientTypeId, +content?.clientInvoiceSituationId, content?.dueDateTime))?.label : '-',
              comments: content?.comments || ''
            }
          });

          await setExportData(formattedData);
        }

        if(exportType == 'extract') {
          const getCategoryName = categoryId => {
            if(!categoryId) {
              return '';
            }

            if(!establishmentId) {
              return noEstablishmentFilters?.categories?.find(category => +category.categoryId == +categoryId)?.categoryName || '-';
            }

            return availableCategories?.find(category => +category?.value == +categoryId)?.label || '';
          }

          const getPaymentMethodName = paymentMethodId => {
            if(!paymentMethodId) {
              return '';
            }

            if(!establishmentId) {
              return noEstablishmentFilters?.paymentMethods?.find(paymentMethod => +paymentMethod.establishmentPaymentMethodId == +paymentMethodId)?.paymentMethodName || '-';
            }

            return availablePaymentMethods?.find(paymentMethod => +paymentMethod?.value == +paymentMethodId)?.label || '';
          }

          let formattedData = [];

          if(!establishmentId) {
            formattedData = data?.content?.map(content => {
              return {
                establishment: `${content?.establishmentId} ${convertToEstablishmentName(content?.establishmentId, establishments)}`,
                orderDateTime: moment(content?.orderDateTime, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY HH:mm:ss'),
                description: content?.description || '',
                amount: convertToReal(content?.amount || ''),
                category: getCategoryName(content?.categoryId),
                paymentMethodId: getPaymentMethodName(content?.paymentMethodId)
              }
            });
          }

          if(!!establishmentId) {
            formattedData = data?.content?.map(content => {
              return {
                orderDateTime: moment(content?.orderDateTime, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY HH:mm:ss'),
                description: content?.description || '',
                amount: convertToReal(content?.amount || ''),
                category: getCategoryName(content?.categoryId),
                paymentMethodId: getPaymentMethodName(content?.paymentMethodId)
              }
            });
          }

          await setExportData(formattedData);
        }

        if(exportType == 'productsSell') {
          const exportedData = data?.map(item => {
            const areasOfUseDescription = areasOfUse
              ?.filter(area => item?.areasOfUse?.includes(area?.id?.toString()))
              ?.map(area => area?.description)
              ?.join(', ');

            return [
              item?.name || '',
              item?.brandName || '',
              item?.productTypeName || '',
              areasOfUseDescription || '',
              numericFormatter(item?.quantityToSell?.toString() || '', { thousandSeparator: '.', decimalSeparator: ',', decimalScale: 2, fixedDecimalScale: 2 }),
              numericFormatter(item?.minQuantityToSell?.toString() || '', { thousandSeparator: '.', decimalSeparator: ',', decimalScale: 2, fixedDecimalScale: 2 }),
              numericFormatter(item?.netContent?.toString() || '', { thousandSeparator: '.', decimalSeparator: ',', decimalScale: 2, fixedDecimalScale: 2 }),
              item?.measurementUnitAbbreviation || ''
            ]
          });

          const exportFormattedData = [
            ['Nome', 'Marca', 'Categoria do Produto', 'Aplicação do Produto', 'Estoque Consumo Atual', 'Estoque Consumo Mínimo', 'Qnd. und. de medida por item', 'Unidade de medida'],
            ...exportedData
          ];

          await setExportData(exportFormattedData);
        }

        if(exportType == 'productsConsume') {
          const exportedData = data?.map(item => {
            const areasOfUseDescription = areasOfUse
              ?.filter(area => item?.areasOfUse?.includes(area?.id?.toString()))
              ?.map(area => area?.description)
              ?.join(', ');

            return [
              item?.name || '',
              item?.brandName || '',
              item?.productTypeName || '',
              areasOfUseDescription || '',
              numericFormatter(item?.quantityToUse?.toString() || '', { thousandSeparator: '.', decimalSeparator: ',', decimalScale: 2, fixedDecimalScale: 2 }),
              numericFormatter(item?.minQuantityToUse?.toString() || '', { thousandSeparator: '.', decimalSeparator: ',', decimalScale: 2, fixedDecimalScale: 2 }),
              numericFormatter(item?.netContent?.toString() || '', { thousandSeparator: '.', decimalSeparator: ',', decimalScale: 2, fixedDecimalScale: 2 }),
              item?.measurementUnitAbbreviation || ''
            ]
          });

          const exportFormattedData = [
            ['Nome', 'Marca', 'Categoria do Produto', 'Aplicação do Produto', 'Estoque Consumo Atual', 'Estoque Consumo Mínimo', 'Qnd. und. de medida por item', 'Unidade de medida'],
            ...exportedData
          ];

          await setExportData(exportFormattedData);
        }

        if(exportType == 'sellOrders') {
          if(data?.length == 0) {
            return toast.error('Nenhuma informação encontrada para exportação.');
          }

          const exportedData = data?.map(item => {
            return [
              item?.clientName,
              formatPhone(item?.clientPhone),
              item?.paymentMethodName,
              `R$ ${numericFormatter(item?.totalAmount?.toString(), { thousandSeparator: '.', decimalSeparator: ',', decimalScale: 2, fixedDecimalScale: 2 })}`,
              item?.situationDescription,
              `R$ ${numericFormatter(item?.productsAmount?.toString(), { thousandSeparator: '.', decimalSeparator: ',', decimalScale: 2, fixedDecimalScale: 2 })}`,
              `R$ ${numericFormatter(item?.discountAmount?.toString(), { thousandSeparator: '.', decimalSeparator: ',', decimalScale: 2, fixedDecimalScale: 2 })}`,
              item?.userName,
              item?.orderItems?.map(order => `${order?.name} (${order?.quantity}) - R$ ${numericFormatter(order?.amount?.toString(), { thousandSeparator: '.', decimalSeparator: ',', decimalScale: 2, fixedDecimalScale: 2 })}`)?.join(', ')
            ]
          });

          const exportFormattedData = [
            ['Cliente', 'Telefone', 'Meio de Pagamento', 'Valor Total', 'Situação', 'Valor dos Produtos', 'Desconto', 'Usuário', 'Pedidos'],
            ...exportedData
          ];

          await setExportData(exportFormattedData);
        }

        if(exportType == 'supplier') {
          if(data?.length == 0) {
            return toast.error('Nenhuma informação encontrada para exportação.');
          }

          const formatSupplierLocation = address => {
            if(!address?.addressNumber && !address?.address && !address?.district && !address?.city) {
              return '-';
            }

            if(!address?.addressNumber) {
              return `${address?.address} - ${address?.district}, ${address?.city}`;
            }
        
            return `${address?.address}, ${address?.addressNumber} - ${address?.district}, ${address?.city}`;
          }

          const exportedData = data?.map(supplier => {
            return {
              name: (Array.isArray(supplier?.contacts) && supplier?.contacts?.length > 0) ? supplier?.contacts[0]?.name : '-',
              fantasyName: supplier?.fantasyName || '-',
              cnpj: supplier?.cnpj || '-',
              phone: (Array.isArray(supplier?.contacts) && supplier?.contacts?.length > 0) ? supplier?.contacts[0]?.phone : '-',
              address: formatSupplierLocation((Array.isArray(supplier?.addresses) && supplier?.addresses?.length > 0) ? supplier?.addresses[0] : [])
            }
          });

          await setExportData(exportedData);
        }

        if(exportType == 'servicesExport') {
          if(data?.length == 0) {
            return toast.error('Nenhuma informação encontrada para exportação.');
          }

          const formattedData = data?.map(item => {
            return {
              categories: formatServicesCategories(item?.categories, servicesCategories),
              description: item?.description || '',
              amount: convertToReal(item?.amount || ''),
              status: +item?.status == 1 ? 'Ativado' : 'Desativado'
            }
          });

          await setExportData(formattedData);
        }

        await exportButton.current.link.click();
      })
      .catch(() => toast.error('Não foi possível exportar, tente novamente mais tarde'));
  }

  return(
    <>
      <Button
        disabled={disabled}
        fullWidth
        disableElevation
        color="inherit"
        loading={isExporting}
        onClick={() => handleExportation()}
      >
        XLS
      </Button>
      <CSVLink
        ref={exportButton}
        headers={(exportType === 'operationalExport') ? null : headers}
        data={exportType === 'operationalExport' ? operationalExportData : exportData}
        separator=";"
        filename={
          (exportType == 'clients' || exportType == 'productsSell' || exportType == 'productsConsume' || exportType == 'sellOrders' || exportType == 'supplier') && establishmentId
            ? getEstablishmentExportName(exportType)
            : `${fileName}.xls`
        }
      />
    </>
  );
}